import { Stack } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { formatDateTime } from "@shared/utils/functions";
import { useState } from "react";

import { InvoicePartyData, PartnerChannelData, PartnerData, PublisherData } from "../utils/types";

import Loader from "@mantis-theme/components/Loader";
import { PUSH_MODULES, InvoicePartiesColumns, InvoiceVisibleColumns, PartnerChannelColumns, PartnerChannelVisibleColumns, PartnerColumns, PartnerVisibleColumns, PublisherVisibleColumns, accordionConfigs } from "../utils/constants";

import { PushModuleAccordionItem } from "./PushModuleAccordionItem";
import { useGetUnPushedData } from "../services/useGetUnPushedData";

// -----------------------------
// Publisher Columns
// -----------------------------
const PublisherColumns: GridColDef<PublisherData>[] = [
  {
    field: "id",
    headerName: "ID",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "jsonConfig",
    headerName: "Json Config",
    flex: 1.5,
    minWidth: 220,
    sortable: false,
    renderCell: (params: GridRenderCellParams<PublisherData, string>) => {
      const text = typeof params.value === "string" ? params.value : JSON.stringify(params.value ?? "");

      const trimmed = text.length > 120 ? text.slice(0, 120) + "…" : text;

      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ overflow: "hidden" }}
        >
          <span
            style={{
              fontFamily: "monospace",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
              maxWidth: "100%",
            }}
            title={text}
          >
            {trimmed}
          </span>
        </Stack>
      );
    },
  },
  {
    field: "createdAt",
    headerName: "Created",
    flex: 1,
    minWidth: 150,
    sortable: false,
    filterable: false,
    hideable: false,
    renderCell: (params: GridRenderCellParams<PublisherData, string>) => {
      return formatDateTime(params.value);
    },
  },
];

// -----------------------------
// Generic Config Type
// -----------------------------
type AccordionConfig<T> = {
  columns: GridColDef<T>[];
  visibleColumns: string[];
  getRowId: (row: T) => string | number;
};

// -----------------------------
// Typed Accordion Config Map
// -----------------------------
const ACCORDION_CONFIG_MAP: {
  [PUSH_MODULES.INVOICE_PARTIES]: AccordionConfig<InvoicePartyData>;
  [PUSH_MODULES.PARTNER_CHANNELS]: AccordionConfig<PartnerChannelData>;
  [PUSH_MODULES.PUBLISHERS]: AccordionConfig<PublisherData>;
  [PUSH_MODULES.PARTNERS]: AccordionConfig<PartnerData>;
} = {
  [PUSH_MODULES.INVOICE_PARTIES]: {
    columns: InvoicePartiesColumns,
    visibleColumns: InvoiceVisibleColumns,
    getRowId: (row) => row.uid,
  },
  [PUSH_MODULES.PARTNER_CHANNELS]: {
    columns: PartnerChannelColumns,
    visibleColumns: PartnerChannelVisibleColumns,
    getRowId: (row) => row.channelId,
  },
  [PUSH_MODULES.PUBLISHERS]: {
    columns: PublisherColumns,
    visibleColumns: PublisherVisibleColumns,
    getRowId: (row) => row.id,
  },
  [PUSH_MODULES.PARTNERS]: {
    columns: PartnerColumns,
    visibleColumns: PartnerVisibleColumns,
    getRowId: (row) => row.referrerID,
  },
};

const TriggerPushAccordion = () => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const { dataMap, isLoading, lastPush } = useGetUnPushedData();

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Stack spacing={3}>
      {accordionConfigs.map(({ id, title, icon }) => {
        const config = ACCORDION_CONFIG_MAP[id];
        const data = dataMap[id] || [];

        return (
          <PushModuleAccordionItem
            key={id}
            id={id}
            title={title}
            icon={icon}
            expanded={expanded === id}
            data={data}
            columns={config.columns}
            visibleColumns={config.visibleColumns}
            getRowId={config.getRowId}
            lastPush={lastPush}
            onChange={handleChange(id)}
          />
        );
      })}
    </Stack>
  );
};

export default TriggerPushAccordion;
